/**
 * Crown01 — info & legal pages.
 *
 * One reusable <InfoPage /> shell + 9 page components below.
 * All pages share consistent SEO, breadcrumb, branding & responsive layout.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://crown01.co.uk";

// ---------- Reusable info-page shell ----------
function InfoPage({ eyebrow, title, lede, lastUpdated, path, description, children }) {
  const fullTitle = `${title} | Crown01`;
  const desc = description || lede || `${title} — Crown01, UK supplier of packaging, cleaning and trade products.`;
  const url = `${SITE_URL}${path}`;
  return (
    <div className="bg-white overflow-x-hidden">
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={url} />
      </Helmet>
      {/* Page header */}
      <div className="border-b" style={{ borderColor: "var(--c-grey-100)", background: "var(--c-cream)" }}>
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="text-xs uppercase tracking-[0.18em] mb-3" style={{ color: "var(--c-gold-deep)" }}>
            <Link to="/" className="hover:underline">Home</Link> · {eyebrow || "Information"}
          </div>
          <h1 className="font-display text-3xl md:text-5xl text-black leading-[1.05]" data-testid="info-title">{title}</h1>
          {lede && <p className="mt-4 text-base md:text-lg text-[color:var(--c-grey-700)] max-w-3xl leading-relaxed">{lede}</p>}
          {lastUpdated && (
            <p className="mt-5 text-xs text-[color:var(--c-grey-500)]">Last updated: {lastUpdated}</p>
          )}
        </div>
      </div>
      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-16 grid md:grid-cols-[200px_1fr] gap-10 min-w-0">
        <aside className="hidden md:block">
          <div className="sticky top-32 text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--c-gold-deep)" }}>Quick links</div>
          <ul className="mt-3 space-y-1.5 text-sm sticky top-44">
            {QUICK_LINKS.map(l => (
              <li key={l.to}>
                <Link to={l.to} className="text-[color:var(--c-grey-700)] hover:text-black hover:underline">{l.label}</Link>
              </li>
            ))}
          </ul>
        </aside>
        <article className="prose-content space-y-6 min-w-0 break-words">
          {children}
          <CompanyTrustStamp />
        </article>
      </div>
    </div>
  );
}

const QUICK_LINKS = [
  { to: "/about", label: "About Us" },
  { to: "/faq", label: "FAQ" },
  { to: "/shipping", label: "Shipping & Delivery" },
  { to: "/returns", label: "Returns & Refunds" },
  { to: "/wholesale", label: "Trade & Wholesale" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms & Conditions" },
  { to: "/cookies", label: "Cookie Policy" },
  { to: "/contact", label: "Contact Us" },
];

function Section({ title, children, id }) {
  return (
    <section id={id} className="scroll-mt-32">
      {title && <h2 className="font-display text-2xl md:text-3xl text-black mb-3">{title}</h2>}
      <div className="text-[color:var(--c-grey-700)] leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function CompanyTrustStamp() {
  return (
    <div className="mt-8 p-5 rounded-2xl text-sm" style={{ background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)" }} data-testid="company-trust-stamp">
      <div className="text-xs uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: "var(--c-gold-deep)" }}>Company Information</div>
      <p className="text-[color:var(--c-grey-700)] leading-relaxed">
        Operated by <strong className="text-black">NAR London Ltd</strong>, a UK-based supplier of packaging, cleaning, catering and wholesale consumable products. We supply retail customers, businesses, wholesalers and pallet buyers across the United Kingdom.
      </p>
      <div className="mt-3 text-xs text-[color:var(--c-grey-500)] leading-relaxed">
        <strong className="text-[color:var(--c-grey-700)]">NAR London Ltd</strong> · trading as <strong className="text-[color:var(--c-grey-700)]">Crown01</strong><br />
        Registered in England and Wales · Company number: <strong className="text-[color:var(--c-grey-700)]">10907445</strong><br />
        95 Theobalds Park Road, Enfield, EN2 9BS, United Kingdom<br />
        <a href="mailto:info@crown01.co.uk" className="underline">info@crown01.co.uk</a> · <a href="https://crown01.co.uk" className="underline">crown01.co.uk</a>
      </div>
    </div>
  );
}

// ---------------------------- ABOUT US ----------------------------
export function AboutPage() {
  return (
    <InfoPage
      eyebrow="About"
      title="About Crown01"
      lede="Operated by NAR London Ltd — a trusted UK supplier of packaging, cleaning, catering and wholesale consumable products. Whether you need 1 pack, 1 box, or 1 pallet, we've got you covered."
      path="/about"
      lastUpdated="January 2026"
    >
      <figure className="mb-8 -mt-2" data-testid="about-team-hero">
        <div className="relative rounded-2xl overflow-hidden shadow-sm" style={{ border: "1px solid var(--c-grey-100)" }}>
          <img
            src="/warehouse/crown01-team-uk-warehouse.jpg"
            alt="The Crown01 team at the UK warehouse in Enfield"
            loading="lazy"
            className="w-full h-auto object-cover block"
            style={{ maxHeight: "440px" }}
          />
          <figcaption
            className="absolute bottom-0 left-0 right-0 px-5 py-4 text-white"
            style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.05) 100%)" }}
          >
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: "var(--c-gold)" }}>The Crown01 Team</div>
            <div className="font-display text-lg md:text-2xl mt-0.5">Real Stock. Real People. Real UK Supplier.</div>
          </figcaption>
        </div>
      </figure>

      <Section title="Who we are">
        <p>
          Crown01 is the trading name of <strong>NAR London Ltd</strong>, a UK-based supplier of high-quality packaging, cleaning, catering and household consumables. We operate from our warehouse in Enfield, North London, and ship across the United Kingdom every working day.
        </p>
        <p>
          We exist to make it just as easy for a household to buy a single pack of bin bags as it is for a restaurant chain to order a pallet — same quality, same fast dispatch, same straightforward pricing.
        </p>
      </Section>

      <Section title="Who we serve">
        <ul className="grid sm:grid-cols-2 gap-2 list-none p-0">
          {[
            "Households & individual customers",
            "Cafés, restaurants & takeaways",
            "Contract cleaners & janitorial companies",
            "Retailers & corner shops",
            "Schools, offices & care homes",
            "Wholesalers & distributors",
            "Trade buyers & pallet customers",
            "Amazon, TikTok Shop & eBay shoppers",
          ].map(t => (
            <li key={t} className="flex items-center gap-2 p-3 rounded-lg" style={{ background: "var(--c-grey-50)" }}>
              <i className="fa-solid fa-check" style={{ color: "var(--c-gold-deep)" }}></i>
              <span className="text-sm">{t}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="What sets us apart">
        <ul className="space-y-2 list-disc pl-5">
          <li><strong className="text-black">Real UK warehouse stock</strong> — same-day dispatch on weekdays, with full visibility of what's in stock.</li>
          <li><strong className="text-black">Pack, box, wholesale, pallet</strong> — buy the quantity that suits your need. No minimum order for retail.</li>
          <li><strong className="text-black">Competitive pricing</strong> — direct from supplier, no middle-men inflating the cost.</li>
          <li><strong className="text-black">Multi-channel</strong> — shop with us via crown01.co.uk, Amazon, TikTok Shop, or eBay.</li>
          <li><strong className="text-black">Real UK customer support</strong> — email reply within 1 business hour.</li>
        </ul>
      </Section>

      <Section title="Our warehouse">
        <p>
          Our products are stocked, picked and dispatched from our UK warehouse:
        </p>
        <div className="mt-2 p-4 rounded-xl" style={{ background: "var(--c-black)", color: "#fff" }}>
          <div className="text-xs uppercase tracking-[0.18em] font-semibold mb-1" style={{ color: "var(--c-gold)" }}>Warehouse Address</div>
          <div className="font-display text-lg">95 Theobalds Park Road</div>
          <div>Enfield, EN2 9BS</div>
          <div>United Kingdom</div>
        </div>
        <p className="mt-3 text-sm text-[color:var(--c-grey-500)]"><em>Please note: our warehouse is not open to walk-in customers. All orders are placed online or via trade enquiry.</em></p>
      </Section>

      <Section title="Get in touch">
        <p>
          For any questions, bulk quotes or trade enquiries, you can reach us at <a href="mailto:info@crown01.co.uk" className="underline font-semibold">info@crown01.co.uk</a> or via our <Link to="/contact" className="underline font-semibold">contact form</Link>. Trade and wholesale buyers can apply for bespoke pricing via our <Link to="/wholesale" className="underline font-semibold">Trade & Wholesale</Link> page.
        </p>
      </Section>
    </InfoPage>
  );
}

// ---------------------------- FAQ ----------------------------
const FAQS = [
  {
    cat: "Ordering & Quantities",
    qs: [
      { q: "Can I buy just one pack?", a: "Yes — there is no minimum order on our retail products. Order a single pack or as many as you need. Single packs typically dispatch the same working day." },
      { q: "What are the box and case quantities?", a: "Many products are available as a single pack or as a full box (often saving 30%+ vs single packs). Box quantities are listed on each product page — for example, refuse sacks come in packs of 10 or boxes of 100." },
      { q: "Do you accept wholesale orders?", a: "Yes. We supply wholesale customers across the UK with competitive, volume-based pricing. Submit a quick application on our Trade & Wholesale page and we'll respond with bespoke pricing within 1 business hour." },
      { q: "Can I order full pallet quantities?", a: "Absolutely — we regularly ship full pallets to contract cleaners, restaurants, distributors and retailers. Pallet quotes are returned within 1 business hour and include custom delivery rates." },
      { q: "How do I check stock availability?", a: "Stock status is shown live on every product page. If a product is in stock it dispatches same-day on weekdays. If you need a large quantity, our team can confirm exact pallet/case availability before you order — contact info@crown01.co.uk." },
    ],
  },
  {
    cat: "Delivery & Shipping",
    qs: [
      { q: "How fast is UK delivery?", a: "Most orders placed before 1pm on a working day are dispatched the same day, with standard delivery typically arriving within 2–3 working days. Express options are available at checkout." },
      { q: "How much is delivery?", a: "Delivery costs are calculated automatically at checkout based on weight and destination. Pallet and bulk shipments are quoted individually. Free standard UK delivery is available on qualifying orders." },
      { q: "Do you deliver to Northern Ireland, the Channel Islands or the Highlands?", a: "Yes — additional carriage may apply for offshore and Highlands postcodes. You'll see the exact rate at checkout before paying." },
      { q: "Can I track my order?", a: "Yes — you'll receive a dispatch confirmation by email with your tracking number once your order leaves the warehouse." },
    ],
  },
  {
    cat: "Returns & Refunds",
    qs: [
      { q: "What's your returns policy?", a: "You can return unopened, unused items within 14 days of delivery for a full refund (Consumer Contracts Regulations 2013). See our Returns & Refund Policy for full details." },
      { q: "What if my order arrives damaged?", a: "Please email info@crown01.co.uk with a photo of the damage within 48 hours of delivery. We'll arrange a replacement or refund at no cost to you." },
    ],
  },
  {
    cat: "Payment & Trade",
    qs: [
      { q: "What payment methods do you accept?", a: "We accept all major debit/credit cards (Visa, Mastercard, American Express), PayPal, Apple Pay and Google Pay via our secure Shopify checkout. Trade customers may be eligible for invoiced payment terms — apply on the Trade & Wholesale page." },
      { q: "Is your checkout secure?", a: "Yes. All payments are processed via Shopify Payments, which is PCI-DSS Level 1 certified. We never see or store your card details." },
      { q: "How do I apply for a trade account?", a: "Submit the form on our Trade & Wholesale page. A trade account manager will email your bespoke pricing within 1 business hour during UK working hours." },
      { q: "How do I get in touch?", a: "Email info@crown01.co.uk or use the form on our Contact Us page. Our UK-based customer support team replies within 1 business hour during working hours (Mon–Fri, 9am–6pm)." },
    ],
  },
];

export function FAQPage() {
  const [open, setOpen] = useState(0); // first item open by default
  let idx = -1;
  return (
    <InfoPage
      eyebrow="Help"
      title="Frequently Asked Questions"
      lede="Everything you need to know about ordering single packs, full boxes, wholesale and pallet quantities from Crown01."
      path="/faq"
      description="Crown01 FAQ — single packs, box quantities, wholesale & pallet orders, UK delivery times, returns, payment methods and trade enquiries."
    >
      {FAQS.map(group => (
        <Section key={group.cat} title={group.cat}>
          <div className="space-y-2.5" data-testid={`faq-group-${group.cat.toLowerCase().replace(/[^a-z]/g, '-')}`}>
            {group.qs.map((qa) => {
              idx++;
              const myIdx = idx;
              const isOpen = open === myIdx;
              return (
                <div key={qa.q} className="rounded-xl overflow-hidden border" style={{ borderColor: isOpen ? "var(--c-gold)" : "var(--c-grey-100)", background: isOpen ? "var(--c-cream)" : "#fff" }}>
                  <button onClick={() => setOpen(isOpen ? -1 : myIdx)} className="w-full flex items-center justify-between gap-4 px-4 md:px-5 py-4 text-left" data-testid={`faq-q-${myIdx}`}>
                    <span className="font-display text-base md:text-lg text-black">{qa.q}</span>
                    <i className={`fa-solid fa-chevron-down text-sm transition-transform ${isOpen ? "rotate-180" : ""}`} style={{ color: "var(--c-gold-deep)" }}></i>
                  </button>
                  {isOpen && (
                    <div className="px-4 md:px-5 pb-4 text-[color:var(--c-grey-700)] leading-relaxed text-sm md:text-base">
                      {qa.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      ))}
      <Section title="Still have a question?">
        <p>
          We're happy to help — email <a href="mailto:info@crown01.co.uk" className="underline font-semibold">info@crown01.co.uk</a> or use our <Link to="/contact" className="underline font-semibold">contact form</Link> and we'll reply within 1 business hour during UK working hours.
        </p>
      </Section>
    </InfoPage>
  );
}

// ---------------------------- PRIVACY POLICY ----------------------------
export function PrivacyPage() {
  return (
    <InfoPage
      eyebrow="Legal"
      title="Privacy Policy"
      lede="How NAR London Ltd (trading as Crown01) collects, uses and protects your personal information."
      path="/privacy"
      lastUpdated="January 2026"
    >
      <Section title="1. Who we are">
        <p><strong>NAR London Ltd</strong>, trading as Crown01, is the data controller for the personal information collected via crown01.co.uk and related sales channels. Registered in England and Wales — Company number <strong>10907445</strong>. Our registered warehouse is at 95 Theobalds Park Road, Enfield, EN2 9BS, United Kingdom. You can contact us about your data at <a href="mailto:info@crown01.co.uk" className="underline">info@crown01.co.uk</a>.</p>
      </Section>
      <Section title="2. Information we collect">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Order details</strong>: name, billing/shipping address, email, phone number, order items (collected via Shopify when you place an order).</li>
          <li><strong>Account / form data</strong>: email for newsletter signup, contact form messages, trade application details.</li>
          <li><strong>Cart data</strong>: items you've added to your basket and the email you provide so we can send abandoned-cart reminders.</li>
          <li><strong>Usage data</strong>: anonymised analytics (pages visited, device type, referrer) via Google Analytics 4 and TikTok Pixel.</li>
        </ul>
      </Section>
      <Section title="3. How we use your information">
        <ul className="list-disc pl-5 space-y-1">
          <li>To process and ship your orders.</li>
          <li>To send order confirmations, dispatch notifications, and customer support replies.</li>
          <li>To send abandoned-cart reminders (you can opt out by replying to any email).</li>
          <li>To send marketing communications if you've opted in via newsletter signup.</li>
          <li>To improve our website and services via aggregate analytics.</li>
          <li>To comply with UK legal and tax obligations.</li>
        </ul>
      </Section>
      <Section title="4. Legal basis for processing">
        <p>Under UK GDPR, we process personal data under one or more of the following lawful bases:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Performance of a contract</strong> — to fulfil your orders and provide the services you've requested.</li>
          <li><strong>Legitimate interests</strong> — website operation, fraud prevention, security and business improvement.</li>
          <li><strong>Consent</strong> — marketing emails and optional analytics/marketing cookies (you can withdraw consent at any time).</li>
          <li><strong>Legal obligation</strong> — tax, accounting and other regulatory requirements.</li>
        </ul>
      </Section>
      <Section title="5. Payment security">
        <p>Payments are processed securely by <strong>Shopify Inc.</strong>, a PCI-DSS Level 1 certified payment platform. Crown01 does not collect, store or process payment card information. When you proceed to checkout, you are redirected to Shopify's hosted checkout where your payment details are entered, encrypted and handled entirely by Shopify and its underlying acquirers. Our systems only receive order metadata (items, quantities, totals and your email address) — never your card number, CVV, expiry date or any other cardholder data.</p>
      </Section>
      <Section title="6. Who we share your information with">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Shopify Inc.</strong> — our checkout, payment and order management provider.</li>
          <li><strong>Resend</strong> — our transactional email provider (order confirmations, customer support replies).</li>
          <li><strong>Google Analytics 4, Meta Pixel and TikTok Pixel</strong> may be used to understand website performance, advertising effectiveness and visitor interactions, subject to your cookie preferences.</li>
          <li><strong>Royal Mail, DPD, UPS and other carriers</strong> — to deliver your orders.</li>
        </ul>
        <p>We do not sell your personal information to third parties.</p>
      </Section>
      <Section title="7. Your rights">
        <p>Under UK GDPR you have the right to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Access the personal information we hold about you</li>
          <li>Request correction of inaccurate information</li>
          <li>Request deletion of your information ("right to be forgotten")</li>
          <li>Object to processing for direct marketing</li>
          <li>Data portability</li>
          <li>Lodge a complaint with the UK Information Commissioner's Office (<a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="underline">ico.org.uk</a>)</li>
        </ul>
        <p>To exercise any of these rights, please email <a href="mailto:info@crown01.co.uk" className="underline">info@crown01.co.uk</a>.</p>
      </Section>
      <Section title="8. Data retention">
        <p>We retain order data for 6 years (HMRC tax requirement). Newsletter subscribers may unsubscribe at any time; we keep an unsubscribe record indefinitely to honour your preference. Abandoned-cart records are deleted 30 days after the cart is created.</p>
      </Section>
      <Section title="9. Cookies">
        <p>See our <Link to="/cookies" className="underline">Cookie Policy</Link> for full details on the cookies we use.</p>
      </Section>
    </InfoPage>
  );
}

// ---------------------------- TERMS & CONDITIONS ----------------------------
export function TermsPage() {
  return (
    <InfoPage
      eyebrow="Legal"
      title="Terms & Conditions"
      lede="The terms under which NAR London Ltd (trading as Crown01) sells goods to you via crown01.co.uk."
      path="/terms"
      lastUpdated="January 2026"
    >
      <Section title="1. About these terms">
        <p>These Terms and Conditions govern the supply of goods from NAR London Ltd ("we", "us", "Crown01") to you, the customer, via crown01.co.uk. NAR London Ltd is registered in England and Wales under company number <strong>10907445</strong>. By placing an order, you accept these Terms in full.</p>
      </Section>
      <Section title="2. The contract">
        <p>An order placed via the website is an offer to buy. We accept your offer once we send you an order confirmation by email. At that point a binding contract is formed.</p>
      </Section>
      <Section title="3. Pricing & payment">
        <p>All prices are shown in GBP and include VAT where applicable. Payment is taken at the point of order via our Shopify-powered checkout. We reserve the right to correct genuine pricing errors and to refuse an order in such cases (you would receive a full refund).</p>
      </Section>
      <Section title="4. Payment security">
        <p>Payments are processed securely by <strong>Shopify Inc.</strong>, a PCI-DSS Level 1 certified payment platform. Crown01 does not collect, store or process payment card information. When you proceed to checkout, you are redirected to Shopify's hosted checkout where your card details are entered, encrypted in transit and handled entirely by Shopify and its underlying acquirers.</p>
      </Section>
      <Section title="5. Delivery">
        <p>Delivery times and costs are explained on our <Link to="/shipping" className="underline">Shipping & Delivery Policy</Link> page. Risk in the goods passes to you on delivery.</p>
      </Section>
      <Section title="6. Returns & cancellation">
        <p>Your right to cancel and our returns process is set out in full on our <Link to="/returns" className="underline">Returns & Refund Policy</Link> page. Nothing in these Terms affects your statutory rights under the Consumer Rights Act 2015.</p>
      </Section>
      <Section title="7. Trade & wholesale">
        <p>Bespoke trade pricing and pallet orders are subject to additional terms communicated by your trade account manager. Net 30 / invoiced terms are available subject to credit checks.</p>
      </Section>
      <Section title="8. Liability">
        <p>Nothing in these Terms limits our liability for death or personal injury caused by our negligence, fraud, or any liability that cannot be limited under UK law. Subject to that, our liability to you for any loss is limited to the price you paid for the goods in question.</p>
      </Section>
      <Section title="9. Force majeure">
        <p>We are not liable for delays or failures caused by events beyond our reasonable control, including but not limited to carrier disruption, industrial action, severe weather, natural disasters, utility failures, cyber incidents, pandemics or government restrictions. Where such an event occurs we will use reasonable efforts to mitigate its impact and keep you informed.</p>
      </Section>
      <Section title="10. Governing law">
        <p>These Terms are governed by the laws of England and Wales, and any dispute will be subject to the exclusive jurisdiction of the English courts.</p>
      </Section>
    </InfoPage>
  );
}

// ---------------------------- RETURNS & REFUND POLICY ----------------------------
export function ReturnsPage() {
  return (
    <InfoPage
      eyebrow="Customer"
      title="Returns & Refund Policy"
      lede="Your right to cancel, return goods, and receive a refund — fully aligned with UK consumer law."
      path="/returns"
      lastUpdated="January 2026"
    >
      <Section title="1. Your 14-day cancellation right">
        <p>Under the Consumer Contracts Regulations 2013, you have the right to cancel your order within <strong>14 days of receiving your goods</strong>, without giving a reason.</p>
      </Section>
      <Section title="2. How to return your order">
        <ol className="list-decimal pl-5 space-y-1">
          <li>Email <a href="mailto:info@crown01.co.uk" className="underline">info@crown01.co.uk</a> with your order number and the items you wish to return.</li>
          <li>We'll send you a returns confirmation and the return address.</li>
          <li>Pack the goods securely and post them back within 14 days of telling us you wish to cancel.</li>
          <li>You are responsible for the cost of return postage (unless the goods are faulty or incorrectly supplied).</li>
        </ol>
      </Section>
      <Section title="3. Refunds">
        <p>Once we receive and inspect your return, we'll process a refund to your original payment method within <strong>5 business days</strong>. The refund will include the price of the goods and any standard outbound delivery you paid (express delivery upgrades are not refunded).</p>
      </Section>
      <Section title="4. Damaged or incorrect items">
        <p>If your order arrives damaged, faulty, or you receive the wrong items, please email <a href="mailto:info@crown01.co.uk" className="underline">info@crown01.co.uk</a> with a photo within 48 hours of delivery. We will arrange a replacement or full refund (including return postage) at no cost to you.</p>
      </Section>
      <Section title="5. Items that cannot be returned">
        <ul className="list-disc pl-5 space-y-1">
          <li>Items that have been opened and used, where the seal cannot be restored</li>
          <li>Bespoke, custom-printed or made-to-order items</li>
          <li>Hygiene products where the seal has been broken</li>
        </ul>
      </Section>
      <Section title="6. Trade & wholesale returns">
        <p>Trade and pallet orders are subject to separate return terms agreed at the point of order. Please speak to your trade account manager.</p>
      </Section>
    </InfoPage>
  );
}

// ---------------------------- SHIPPING & DELIVERY ----------------------------
export function ShippingPage() {
  return (
    <InfoPage
      eyebrow="Customer"
      title="Shipping & Delivery Policy"
      lede="Fast, tracked UK delivery from our Enfield warehouse — same-day dispatch on most working-day orders."
      path="/shipping"
      lastUpdated="January 2026"
    >
      <Section title="1. Delivery area">
        <p>1.1. We currently deliver within the <strong>United Kingdom only</strong>.</p>
        <p>1.2. All orders are dispatched from our fulfilment location in the United Kingdom (95 Theobalds Park Road, Enfield, EN2 9BS).</p>
      </Section>

      <Section title="2. Order processing">
        <p>2.1. Orders are processed within <strong>1–2 business days</strong> (Monday to Friday, excluding public holidays). Orders placed before 1pm on a working day are typically dispatched the same day.</p>
        <p>2.2. Once dispatched, you will receive a confirmation email. Tracking details will be provided where available.</p>
      </Section>

      <Section title="3. Delivery timeframes">
        <p>3.1. Estimated delivery times after dispatch:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Standard Delivery</strong>: 2–4 business days</li>
          <li><strong>Express Delivery</strong>: 1–3 business days (where selected)</li>
        </ul>
        <p>3.2. Delivery times are estimates only and may vary due to circumstances beyond our control, including courier delays, peak periods or adverse weather conditions.</p>
      </Section>

      <Section title="4. Shipping charges (United Kingdom)">
        <p>Shipping charges are calculated at checkout based on the total weight of your order.</p>
        <div className="overflow-x-auto -mx-2 mt-2">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--c-grey-50)" }}>
                <th className="text-left px-3 py-2 font-semibold">Order weight</th>
                <th className="text-left px-3 py-2 font-semibold">Standard rate</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">0 – 5 kg</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">£3.99</td></tr>
              <tr><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">5.01 – 10 kg</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">£6.49</td></tr>
              <tr><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">10.01 – 15 kg</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">£9.99</td></tr>
              <tr><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">15.01 – 20 kg</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">£13.30</td></tr>
              <tr><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">20.01 – 30 kg</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">£20.40</td></tr>
              <tr><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">30.01 – 45 kg</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">£25.89</td></tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[color:var(--c-grey-500)] mt-2">Orders exceeding 45 kg may require pallet delivery. In such cases, customers will be contacted with a custom delivery quotation before dispatch. Surcharges may apply for the Scottish Highlands &amp; Islands (AB, IV, KW, PA, PH, HS, ZE), Northern Ireland (BT), Isle of Man (IM), Isle of Wight (PO30–PO41) and Channel Islands — exact rates are shown at checkout.</p>
      </Section>

      <Section title="5. Bulk &amp; pallet orders">
        <p>5.1. Orders exceeding 45 kg may require pallet or freight delivery.</p>
        <p>5.2. We reserve the right to adjust shipping charges for bulk or pallet orders if required by courier or freight costs.</p>
        <p>5.3. Customers may contact us prior to placing large bulk orders to obtain a delivery quote — email <a href="mailto:info@crown01.co.uk" className="underline">info@crown01.co.uk</a>.</p>
      </Section>

      <Section title="6. Incorrect address &amp; failed delivery">
        <p>6.1. Customers are responsible for providing accurate delivery information.</p>
        <p>6.2. We are not liable for delays or failed deliveries caused by incorrect or incomplete addresses.</p>
        <p>6.3. If an order is returned due to incorrect address details, additional shipping charges may apply for re-delivery.</p>
      </Section>

      <Section title="7. Risk &amp; title">
        <p>7.1. Risk of loss or damage passes to the customer upon delivery.</p>
        <p>7.2. Ownership of goods remains with us until full payment has been received.</p>
      </Section>

      <Section title="8. Courier services">
        <p>8.1. We use third-party courier providers including (but not limited to) Royal Mail, Evri, DPD and other logistics partners.</p>
        <p>8.2. Once an order has been dispatched, we are not responsible for delays caused by courier services.</p>
      </Section>

      <Section title="9. Policy updates">
        <p>We reserve the right to update this Shipping Policy at any time. Changes take effect immediately upon publication on our website.</p>
      </Section>
    </InfoPage>
  );
}

// ---------------------------- COOKIE POLICY ----------------------------
export function CookiesPage() {
  return (
    <InfoPage
      eyebrow="Legal"
      title="Cookie Policy"
      lede="The cookies and similar technologies we use on crown01.co.uk, and how to control them."
      path="/cookies"
      lastUpdated="January 2026"
    >
      <Section title="1. What are cookies?">
        <p>Cookies are small text files placed on your device by websites you visit. They help sites remember your preferences, keep you signed in, and understand how the site is used.</p>
      </Section>
      <Section title="2. Cookies we use">
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--c-grey-50)" }}>
                <th className="text-left px-3 py-2 font-semibold">Cookie</th>
                <th className="text-left px-3 py-2 font-semibold">Purpose</th>
                <th className="text-left px-3 py-2 font-semibold">Type</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">crown01_cart</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">Remembers items in your basket between visits</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">Functional</td></tr>
              <tr><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">crown01_known_email</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">Remembers your email after signup so we can send cart reminders</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">Functional</td></tr>
              <tr><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">_ga, _ga_*</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">Google Analytics 4 — anonymous usage statistics</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">Analytics</td></tr>
              <tr><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">_fbp</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">Meta (Facebook) Pixel — measures ad effectiveness</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">Marketing</td></tr>
              <tr><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">_ttp</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">TikTok Pixel — measures ad effectiveness</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">Marketing</td></tr>
              <tr><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">_shopify_*</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">Set by Shopify to enable secure checkout and remember your basket</td><td className="px-3 py-2 border-t border-[color:var(--c-grey-100)]">Strictly necessary</td></tr>
            </tbody>
          </table>
        </div>
      </Section>
      <Section title="3. Managing cookies">
        <p>You can change your consent at any time by clicking <strong>Cookie Preferences</strong> in the website footer. Necessary cookies cannot be turned off — they are required for the basket and secure checkout.</p>
        <p>You can also block or delete cookies via your browser settings. For analytics/marketing cookies, you can opt out via:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Google Analytics: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="underline">Google Opt-Out tool</a></li>
          <li>Meta: Adjust your <a href="https://www.facebook.com/adpreferences" target="_blank" rel="noopener noreferrer" className="underline">Meta Ad Preferences</a></li>
          <li>TikTok: Adjust your <a href="https://www.tiktok.com/safety/en/ads-and-data" target="_blank" rel="noopener noreferrer" className="underline">TikTok ad settings</a></li>
        </ul>
      </Section>
      <Section title="4. Updates to this policy">
        <p>We may update this Cookie Policy from time to time. Material changes will be communicated on this page.</p>
      </Section>
    </InfoPage>
  );
}

// ---------------------------- TRADE & WHOLESALE INFORMATION ----------------------------
export function WholesaleInfoPage() {
  return (
    <InfoPage
      eyebrow="Trade"
      title="Trade & Wholesale Information"
      lede="Bespoke pricing, dedicated account management and pallet delivery for restaurants, contract cleaners, retailers and distributors across the UK."
      path="/wholesale"
      description="Learn about Crown01's trade and wholesale programme — tiered pricing, pallet shipments, dedicated account managers and fast UK delivery for trade buyers."
    >
      <Section title="Who qualifies">
        <p>Our trade programme is open to any UK business that orders consumables regularly, including:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Restaurants, cafés, takeaways and food trucks</li>
          <li>Contract cleaners and janitorial companies</li>
          <li>Independent retailers, corner shops and convenience stores</li>
          <li>Schools, offices, care homes and hospitality groups</li>
          <li>Wholesalers, resellers and distributors</li>
        </ul>
        <p>There is no minimum company size — what matters is the volume of consumables you go through.</p>
      </Section>
      <Section title="How trade pricing works">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Tier 1 — Pack/Box</strong>: standard retail prices, no minimum order.</li>
          <li><strong>Tier 2 — Case discount</strong>: better pricing when ordering full cases of any product.</li>
          <li><strong>Tier 3 — Wholesale</strong>: bespoke pricing for regular monthly orders (typically £1,000+/month).</li>
          <li><strong>Tier 4 — Pallet</strong>: full pallet pricing for large operations and contract suppliers.</li>
        </ul>
      </Section>
      <Section title="What you get">
        <ul className="list-disc pl-5 space-y-1">
          <li>Bespoke, transparent pricing on the products you use most</li>
          <li>Dedicated UK account manager you can email or call directly</li>
          <li>Net 30 invoiced terms available subject to credit checks</li>
          <li>Pallet delivery with kerbside or tail-lift options</li>
          <li>Same fast UK dispatch as retail — usually same-day from our Enfield warehouse</li>
        </ul>
      </Section>
      <Section title="How to apply">
        <p>Submit the short application form on our <Link to="/trade" className="underline font-semibold">Apply for a Trade Account</Link> page — it takes about 60 seconds. A trade account manager will email your bespoke pricing within 1 business hour during UK working hours.</p>
        <div className="mt-4">
          <Link to="/trade" className="btn btn-red" data-testid="wholesale-apply-cta">
            Apply for Trade Pricing <i className="fa-solid fa-arrow-right"></i>
          </Link>
        </div>
      </Section>
      <Section title="Speak to us">
        <p>Already in the trade programme, or want to chat through your requirements first? Email <a href="mailto:info@crown01.co.uk" className="underline">info@crown01.co.uk</a> or use our <Link to="/contact" className="underline">contact form</Link>.</p>
      </Section>
    </InfoPage>
  );
}
